import React from 'react';
import { cn } from '@/lib/utils';
import { RiqueDataTable, tryParseGfmTable } from '@/components/Rique/RiqueDataTable';

function inlineFormat(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  const re = /(\*\*[^*]+\*\*|`[^`]+`|\*[^*]+\*)/g;
  let last = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = re.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    const token = match[0];
    if (token.startsWith('**')) {
      parts.push(
        <strong key={key++} className="font-semibold">
          {token.slice(2, -2)}
        </strong>
      );
    } else if (token.startsWith('`')) {
      parts.push(
        <code key={key++} className="rounded bg-muted px-1 py-0.5 font-mono text-[0.85em]">
          {token.slice(1, -1)}
        </code>
      );
    } else if (token.startsWith('*')) {
      parts.push(<em key={key++}>{token.slice(1, -1)}</em>);
    }
    last = match.index + token.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

interface RiqueMarkdownProps {
  content: string;
  className?: string;
}

/** Markdown leve para respostas do Rique (listas, negrito, código, tabelas GFM). */
export const RiqueMarkdown: React.FC<RiqueMarkdownProps> = ({ content, className }) => {
  const lines = content.split('\n');
  const blocks: React.ReactNode[] = [];
  let listItems: string[] = [];
  let key = 0;
  let i = 0;

  const flushList = () => {
    if (listItems.length === 0) return;
    blocks.push(
      <ul key={key++} className="my-1.5 list-disc space-y-0.5 pl-4">
        {listItems.map((item, idx) => (
          <li key={idx}>{inlineFormat(item)}</li>
        ))}
      </ul>
    );
    listItems = [];
  };

  while (i < lines.length) {
    const line = lines[i]!;
    const trimmed = line.trim();

    if (!trimmed) {
      flushList();
      i += 1;
      continue;
    }

    const table = tryParseGfmTable(lines, i);
    if (table) {
      flushList();
      blocks.push(
        <RiqueDataTable
          key={key++}
          columns={table.columns}
          rows={table.rows}
          showActions={table.rows.length > 0}
        />
      );
      i = table.end;
      continue;
    }

    if (/^[-*]\s+/.test(trimmed)) {
      listItems.push(trimmed.replace(/^[-*]\s+/, ''));
      i += 1;
      continue;
    }

    flushList();

    if (trimmed.startsWith('### ')) {
      blocks.push(
        <h4 key={key++} className="mt-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {trimmed.slice(4)}
        </h4>
      );
    } else if (trimmed.startsWith('## ')) {
      blocks.push(
        <h3 key={key++} className="mt-2 text-sm font-semibold text-foreground">
          {trimmed.slice(3)}
        </h3>
      );
    } else if (trimmed.startsWith('# ')) {
      blocks.push(
        <h2 key={key++} className="mt-2 text-base font-semibold text-foreground">
          {trimmed.slice(2)}
        </h2>
      );
    } else {
      blocks.push(
        <p key={key++} className="leading-relaxed">
          {inlineFormat(line)}
        </p>
      );
    }
    i += 1;
  }
  flushList();

  return <div className={cn('space-y-0.5 text-[15px]', className)}>{blocks}</div>;
};
